/**
 * User-editable default WRITE scope, per resource.
 *
 * The product default for how visible a NEW overlay is (a created label/tag/
 * group/entity, or an attach) lives in `OVERLAY_CONFIG[resource].defaultWriteScope`
 * (see `@/config`). This facade layers the CALLER'S OWN choice on top: a small
 * `{ resource -> tier }` map that now lives in the shared, server-backed
 * {@link useDashboardPrefs} store (a `saved-views-api` `user` row, cached locally
 * in `ScopedStorage`), with the product config as the fallback via the pure
 * `resolveWriteScope`.
 *
 * Kept as its own hook (not folded into the store) so its existing call sites —
 * the catalog auto-attach adapter, graph right-click assignment, the address
 * page, Settings — are untouched and keep a stable, memoised surface.
 */
import * as React from "react";
import {
  resolveWriteScope,
  type OverlayResource,
  type OverlayWriteScope,
} from "@/config";
import { useDashboardPrefs, type WriteScopeMap } from "./dashboardPrefs";

export interface WriteScopePrefs {
  /** The caller's saved per-resource overrides (product defaults omitted). */
  overrides: WriteScopeMap;
  /** Effective default for `resource`: override (if valid) else product default. */
  resolveDefaultWriteScope: (resource: OverlayResource) => OverlayWriteScope;
  /** Persist an override; pass `null` to clear it back to the product default. */
  setDefaultWriteScope: (resource: OverlayResource, scope: OverlayWriteScope | null) => void;
  /** True when the caller has explicitly overridden the product default. */
  isOverridden: (resource: OverlayResource) => boolean;
}

/** Access the reactive per-resource write-scope defaults. */
export function useWriteScopePrefs(): WriteScopePrefs {
  const { blob, setWriteScope } = useDashboardPrefs();
  const overrides = blob.writeScope;
  return React.useMemo<WriteScopePrefs>(
    () => ({
      overrides,
      resolveDefaultWriteScope: (resource) => resolveWriteScope(overrides, resource),
      setDefaultWriteScope: setWriteScope,
      isOverridden: (resource) => overrides[resource] != null,
    }),
    [overrides, setWriteScope],
  );
}
